import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash, PuzzlePiece, Tag, ChatCircleText } from "@phosphor-icons/react";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { TenantPicker } from "./TenantPicker";
import { money, fmtDate, REQUEST_STATUS, REQUEST_ORDER } from "../../../lib/bespoke";

/** Console-side: log a request that came in by phone / e-mail. */
export function LogRequestDialog({ open, onOpenChange, onSaved }) {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ title: "", details: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!tenant) return toast.error("Pick the tenant who asked");
    setBusy(true);
    try {
      const { data } = await API.post("/platform/bespoke/requests", { tenant_id: tenant.id, ...form });
      toast.success("Request logged");
      onSaved?.(data); onOpenChange(false); setTenant(null); setForm({ title: "", details: "", contact: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log the request");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bespoke-log-request-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Log a request</DialogTitle><DialogDescription>For requests that arrived outside the app — a call, an e-mail, a meeting.</DialogDescription></DialogHeader>
        <TenantPicker value={tenant} onChange={setTenant} testId="bespoke-log-tenant" />
        <div className="space-y-3">
          <div><Label className="text-xs">What they want *</Label><Input value={form.title} onChange={set("title")} maxLength={120} placeholder="e.g. Waste list for quoting clearances" className="mt-1" data-testid="bespoke-log-title" /></div>
          <div><Label className="text-xs">Details *</Label><Textarea value={form.details} onChange={set("details")} rows={4} maxLength={4000} className="mt-1" data-testid="bespoke-log-details" /></div>
          <div><Label className="text-xs">Who asked (name / e-mail / phone)</Label><Input value={form.contact} onChange={set("contact")} maxLength={200} className="mt-1" data-testid="bespoke-log-contact" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="bespoke-log-request-save">{busy ? "Saving…" : "Log request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDialog({ req, onClose, onDone }) {
  const [form, setForm] = useState({ one_off_fee: req.quote?.one_off_fee ?? "", monthly_fee: req.quote?.monthly_fee ?? "", note: req.quote?.note ?? "", status: req.status === "new" ? "quoted" : req.status });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await API.put(`/platform/bespoke/requests/${req.id}`, { quote: { one_off_fee: Number(form.one_off_fee || 0), monthly_fee: Number(form.monthly_fee || 0), note: form.note }, status: form.status });
      toast.success(form.status === "quoted" ? "Quote sent to the tenant's admins" : "Quote saved");
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the quote"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bespoke-quote-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Quote — {req.title}</DialogTitle><DialogDescription>{req.tenant_name}. Marking it <b>quoted</b> e-mails the tenant's admins the figures.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">One-off build fee (£)</Label><Input type="number" min="0" step="0.01" value={form.one_off_fee} onChange={set("one_off_fee")} className="mt-1" data-testid="bespoke-quote-oneoff" /></div>
          <div><Label className="text-xs">Monthly fee (£)</Label><Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={set("monthly_fee")} className="mt-1" data-testid="bespoke-quote-monthly" /></div>
          <div className="col-span-2"><Label className="text-xs">Note to the tenant</Label><Textarea value={form.note} onChange={set("note")} rows={3} maxLength={600} placeholder="What's included, timescale, anything they need to confirm" className="mt-1" data-testid="bespoke-quote-note" /></div>
          <div className="col-span-2">
            <Label className="text-xs">Status after saving</Label>
            <select value={form.status} onChange={set("status")} className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="bespoke-quote-status">
              {REQUEST_ORDER.map((s) => <option key={s} value={s}>{REQUEST_STATUS[s].label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="bespoke-quote-save">{busy ? "Saving…" : "Save quote"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestCard({ r, onChanged, onBuild }) {
  const [quoting, setQuoting] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const setStatus = async (status) => {
    if (status === r.status) return;
    const note = status === "declined" ? window.prompt("Reason (sent to the tenant's admins):", "") : "";
    if (status === "declined" && note === null) return;
    try {
      await API.put(`/platform/bespoke/requests/${r.id}`, { status, note: note || "" });
      toast.success(`Request marked ${REQUEST_STATUS[status].label.toLowerCase()}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update the request"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the request '${r.title}'?`)) return;
    try { await API.delete(`/platform/bespoke/requests/${r.id}`); toast.success("Request deleted"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`bespoke-request-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">{r.title}</h3>
            <Badge className={`text-[9px] ${REQUEST_STATUS[r.status]?.cls}`} data-testid={`bespoke-request-status-${r.id}`}>{REQUEST_STATUS[r.status]?.label || r.status}</Badge>
            <Badge variant="outline" className="text-[9px]">{r.source === "tenant" ? "from the app" : "logged by console"}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            <b>{r.tenant_name}</b>{r.tenant_slug ? <Link to={`/platform/tenants?q=${r.tenant_slug}`} className="ml-1 hover:underline">/{r.tenant_slug}</Link> : null} · {r.requested_by_name || r.contact || "—"}{r.requested_by_email ? ` (${r.requested_by_email})` : ""} · {fmtDate(r.created_at)}
          </p>
          <p className={`text-sm mt-2 whitespace-pre-wrap ${openDetails ? "" : "line-clamp-3"}`} data-testid={`bespoke-request-details-${r.id}`}>{r.details}</p>
          {r.details.length > 200 && <button type="button" className="text-xs text-primary hover:underline mt-1" onClick={() => setOpenDetails((o) => !o)}>{openDetails ? "Show less" : "Show more"}</button>}
          {r.quote && (r.quote.one_off_fee > 0 || r.quote.monthly_fee > 0 || r.quote.note) && (
            <p className="text-xs mt-2 rounded-md bg-muted px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid={`bespoke-request-quote-${r.id}`}><Tag size={12} /> Quoted {money(r.quote.one_off_fee)} set-up + {money(r.quote.monthly_fee)}/month{r.quote.note ? ` — ${r.quote.note}` : ""}</p>
          )}
          {r.module_id && <p className="text-xs mt-2"><Link to={`/platform/bespoke/${r.module_id}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`bespoke-request-module-${r.id}`}><PuzzlePiece size={12} /> Open the module</Link></p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <select value={r.status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`bespoke-request-status-select-${r.id}`}>
            {REQUEST_ORDER.map((s) => <option key={s} value={s}>{REQUEST_STATUS[s].label}</option>)}
          </select>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setQuoting(true)} data-testid={`bespoke-request-quote-btn-${r.id}`}><Tag size={12} className="mr-1" /> Quote</Button>
            {!r.module_id && r.status !== "declined" && <Button size="sm" className="h-7 text-xs" onClick={() => onBuild(r)} data-testid={`bespoke-request-build-btn-${r.id}`}><PuzzlePiece size={12} className="mr-1" /> Create module</Button>}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={remove} title="Delete" data-testid={`bespoke-request-delete-${r.id}`}><Trash size={13} /></Button>
          </div>
        </div>
      </div>
      {(r.history || []).length > 1 && (
        <details className="mt-3">
          <summary className="text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer inline-flex items-center gap-1"><ChatCircleText size={11} /> History ({r.history.length})</summary>
          <ul className="mt-1 space-y-0.5">{[...r.history].reverse().map((h, i) => <li key={i} className="text-[11px] text-muted-foreground">{fmtDate(h.at)} · {REQUEST_STATUS[h.status]?.label || h.status}{h.by ? ` · ${h.by}` : ""}{h.note ? ` — ${h.note}` : ""}</li>)}</ul>
        </details>
      )}
      {quoting && <QuoteDialog req={r} onClose={() => setQuoting(false)} onDone={onChanged} />}
    </div>
  );
}

export function RequestsBoard({ requests, onChanged, onBuild }) {
  const [filter, setFilter] = useState("open");
  const shown = requests.filter((r) => filter === "all" || (filter === "open" ? ["new", "quoted", "building"].includes(r.status) : r.status === filter));
  return (
    <div className="space-y-3" data-testid="bespoke-requests-board">
      <div className="flex flex-wrap gap-1.5">
        {[["open", "Open"], ["all", "All"], ...REQUEST_ORDER.map((s) => [s, REQUEST_STATUS[s].label])].map(([k, label]) => {
          const n = k === "all" ? requests.length : k === "open" ? requests.filter((r) => ["new", "quoted", "building"].includes(r.status)).length : requests.filter((r) => r.status === k).length;
          return <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${filter === k ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-muted"}`} data-testid={`bespoke-requests-filter-${k}`}>{label} · {n}</button>;
        })}
      </div>
      {shown.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center rounded-xl border border-dashed border-border" data-testid="bespoke-requests-empty">No requests here. Tenants can ask from their Billing page; you can also log one that came in by phone or e-mail.</p>}
      {shown.map((r) => <RequestCard key={r.id} r={r} onChanged={onChanged} onBuild={onBuild} />)}
    </div>
  );
}
